import React from "react";
import { SideWidget } from "./SideWidget";
import type { LayoutType } from "../../store/slices/uiSlice";
import type { Widget } from "../../types";
import { useStore } from "../../store";

interface LayoutRendererProps {
  layout: LayoutType;
  widgets: Widget[];
  // Absolute index offset of the first widget rendered in this layout
  baseOffset?: number;
}

export const LayoutRenderer: React.FC<LayoutRendererProps> = ({
  layout,
  widgets,
  baseOffset = 0,
}) => {
  const {
    highlightedElement,
    isSuggestingWidget,
    suggestedIndex,
    assignSuggestedToIndex,
  } = useStore();
  const getCell = (index: number, size: "1x1" | "2x1" | "2x2" = "1x1") => (
    <SideWidget key={index} widget={widgets[index]} size={size} />
  );

  const isHighlighted = highlightedElement === "side-widgets";

  // Map layouts to absolute row-start indices for suggestion flow
  const getRowStartIndices = (): [number, number] => {
    switch (layout) {
      case "2-2":
        return [baseOffset + 0, baseOffset + 2];
      case "2-1":
        return [baseOffset + 0, baseOffset + 2];
      case "1-2":
        return [baseOffset + 0, baseOffset + 1];
      case "1-1":
        return [baseOffset + 0, baseOffset + 1];
      case "1":
        // Act as if there are two 2x1 rows; both map to the single slot
        return [baseOffset + 0, baseOffset + 0];
      default:
        return [baseOffset + 0, baseOffset + 2];
    }
  };

  const [topRowStart, bottomRowStart] = getRowStartIndices();

  // Always use a 2x2 grid with fixed row heights; control placement via spans
  return (
    <div
      className={`grid grid-cols-2 grid-rows-2 gap-4 auto-rows-[1fr] w-[520px] relative ${
        isHighlighted ? "border-4 border-crane-accent animate-pulse-border" : ""
      }`}
    >
      {isSuggestingWidget && (
        <>
          {/* Top row overlay */}
          <button
            type="button"
            onClick={() => assignSuggestedToIndex(topRowStart)}
            className={`absolute left-0 right-0 top-0 h-1/2 z-30 rounded ${
              suggestedIndex === topRowStart
                ? "border-2 border-crane-selection-blue bg-black/40"
                : "bg-black/30 hover:bg-black/40"
            }`}
            title="Assign suggested widget to this row"
          />
          {/* Bottom row overlay */}
          <button
            type="button"
            onClick={() => assignSuggestedToIndex(bottomRowStart)}
            className={`absolute left-0 right-0 bottom-0 h-1/2 z-30 rounded ${
              suggestedIndex === bottomRowStart
                ? "border-2 border-crane-selection-blue bg-black/40"
                : "bg-black/30 hover:bg-black/40"
            }`}
            title="Assign suggested widget to this row"
          />
        </>
      )}
      {isHighlighted && (
        <div className="absolute inset-0 bg-crane-accent/50 z-30 flex items-center justify-center">
          <div className="text-white text-[30px] ">Placeholders</div>
        </div>
      )}
      {layout === "2-2" && (
        <>
          {getCell(0, "1x1")}
          {getCell(1, "1x1")}
          {getCell(2, "1x1")}
          {getCell(3, "1x1")}
        </>
      )}

      {layout === "2-1" && (
        <>
          {getCell(0, "1x1")}
          {getCell(1, "1x1")}
          <div className="col-span-2  row-span-1">{getCell(2, "2x1")}</div>
        </>
      )}

      {layout === "1-2" && (
        <>
          <div className="col-span-2 row-span-1">{getCell(0, "2x1")}</div>
          {getCell(1, "1x1")}
          {getCell(2, "1x1")}
        </>
      )}

      {layout === "1-1" && (
        <>
          <div className="col-span-2 row-span-1">{getCell(0, "2x1")}</div>
          <div className="col-span-2 row-span-1">{getCell(1, "2x1")}</div>
        </>
      )}

      {layout === "1" && (
        <div className="col-span-2 row-span-2 ">{getCell(0, "2x2")}</div>
      )}
    </div>
  );
};
